import type { DiscoveryApi, FetchApi } from '@mainstage/core-plugin-api';
import crossFetch from 'cross-fetch';
import type { ControlPlaneApi } from './ControlPlaneApi';
import type {
  DynamicEnrichmentProvider,
  DynamicEnrichmentResponse,
  ListDynamicEnrichmentProvidersRequest,
  PaginatedDynamicEnrichmentProviders,
  UpdateDynamicEnrichmentProviderRequest,
} from './zodSchema';

export interface ControlPlaneClientOptions {
  discoveryApi: DiscoveryApi;
  fetchApi?: FetchApi;
  /** Token factory. Used when calling the API on behalf of the current user session. */
  getToken?: () => Promise<string | undefined>;
}

/**
 * HTTP client for the wb-control-plane backend API.
 * Intended for use in the frontend or in other backend plugins.
 *
 * @public
 */
export class ControlPlaneClient implements ControlPlaneApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;
  private readonly getToken: () => Promise<string | undefined>;

  constructor(options: ControlPlaneClientOptions) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi ?? { fetch: crossFetch as typeof fetch };
    this.getToken = options.getToken ?? (() => Promise.resolve(undefined));
  }

  private async getBaseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('control-plane');
  }

  private async authHeaders(): Promise<Record<string, string>> {
    const token = await this.getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  private async throwWithParsedError(
    response: Response,
    operation: string,
  ): Promise<never> {
    const fallback = `${response.status} ${response.statusText}`.trim();
    const contentType = response.headers.get('content-type') ?? '';
    let detail = fallback;

    try {
      if (contentType.includes('application/json')) {
        const body = (await response.json()) as {
          message?: unknown;
        };
        if (typeof body.message === 'string' && body.message.trim()) {
          detail = body.message;
        }
      } else {
        const text = (await response.text()).trim();
        if (text) {
          detail = text;
        }
      }
    } catch {
      // Keep fallback status text if body parsing fails.
    }
    throw new Error(`${operation}: ${detail}`);
  }

  async getDynamicEnrichmentForProject(
    urn: string,
    env: string,
  ): Promise<DynamicEnrichmentResponse> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/v1/projects/${encodeURIComponent(
      urn,
    )}/dynamic-data?env=${encodeURIComponent(env)}`;
    const resp = await this.fetchApi.fetch(url, {
      headers: { ...(await this.authHeaders()), Accept: 'application/json' },
    });
    if (!resp.ok) {
      return this.throwWithParsedError(resp, 'Failed to get dynamic data');
    }
    return resp.json() as Promise<DynamicEnrichmentResponse>;
  }

  async listProviders(
    params?: ListDynamicEnrichmentProvidersRequest,
  ): Promise<PaginatedDynamicEnrichmentProviders> {
    const baseUrl = await this.getBaseUrl();
    const source = params?.source;
    const offset = params?.offset ?? 0;
    const limit = params?.limit ?? 20;
    const query = new URLSearchParams({
      offset: String(offset),
      limit: String(limit),
    });
    if (source) {
      query.set('source', source);
    }
    const url = `${baseUrl}/v1/administration/dynamic-data/providers?${query.toString()}`;
    const resp = await this.fetchApi.fetch(url, {
      headers: { ...(await this.authHeaders()), Accept: 'application/json' },
    });
    if (!resp.ok) {
      return this.throwWithParsedError(
        resp,
        'Failed to list descriptor enrichment providers',
      );
    }
    return resp.json() as Promise<PaginatedDynamicEnrichmentProviders>;
  }

  async getProvider(id: string): Promise<DynamicEnrichmentProvider> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/v1/administration/dynamic-data/providers/${encodeURIComponent(
      id,
    )}`;
    const resp = await this.fetchApi.fetch(url, {
      headers: { ...(await this.authHeaders()), Accept: 'application/json' },
    });
    if (!resp.ok) {
      return this.throwWithParsedError(
        resp,
        `Failed to retrieve descriptor enrichment provider with ID '${id}'`,
      );
    }
    return resp.json() as Promise<DynamicEnrichmentProvider>;
  }

  async updateProvider(
    id: string,
    patch: UpdateDynamicEnrichmentProviderRequest,
  ): Promise<DynamicEnrichmentProvider> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/v1/administration/dynamic-data/providers/${encodeURIComponent(
      id,
    )}`;
    const resp = await this.fetchApi.fetch(url, {
      method: 'PATCH',
      headers: {
        ...(await this.authHeaders()),
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(patch),
    });
    if (!resp.ok) {
      return this.throwWithParsedError(
        resp,
        `Failed to update descriptor enrichment provider with ID '${id}'`,
      );
    }
    return resp.json() as Promise<DynamicEnrichmentProvider>;
  }
}
